import { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import * as v from 'valibot';
import type { RootState } from '@redux/RootReducer';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { DEFAULT_MONTH_RANGE, getLocalTimeZone, toZonedDayIso } from '@/utils/date';
import { cleanNuqsURLParams } from '@/utils/url';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { normalizeAccessorialsFilters, normalizeAccessorialsFiltersUpdate } from '../data-provider';
import { type AccessorialsFilters, type AccessorialsFiltersApi, accessorialsFiltersApiSchema } from '../models';

const DEFAULT_TIME_ZONE = getLocalTimeZone();
const DEFAULT_EFFECTIVE_FROM_DATE = toZonedDayIso(DEFAULT_TIME_ZONE);
const DEFAULT_EFFECTIVE_TO_DATE = toZonedDayIso(DEFAULT_TIME_ZONE, DEFAULT_MONTH_RANGE, true);

const parsers = {
  accessorialCode: parseAsString,
  accessorialKey: parseAsInteger,
  branchKey: parseAsInteger,
  carrierKey: parseAsInteger,
  currencyCode: parseAsString,
  customerKey: parseAsInteger.withDefault(0),
  dangerousGoods: parseAsBoolean,
  destinationCity: parseAsString,
  destinationCountry: parseAsString,
  destinationLocationKey: parseAsInteger,
  destinationPortName: parseAsString,
  destinationPostalFrom: parseAsString,
  destinationPostalTo: parseAsString,
  destinationState: parseAsString,
  directInterLine: parseAsString,
  effectiveFromDate: parseAsString.withDefault(DEFAULT_EFFECTIVE_FROM_DATE),
  effectiveToDate: parseAsString.withDefault(DEFAULT_EFFECTIVE_TO_DATE),
  endEffectiveToDate: parseAsString,
  laneKey: parseAsInteger,
  originCity: parseAsString,
  originCountry: parseAsString,
  originLocationKey: parseAsInteger,
  originPortName: parseAsString,
  originPostalFrom: parseAsString,
  originPostalTo: parseAsString,
  originState: parseAsString,
  pricingType: parseAsString,
  serviceKey: parseAsInteger,
  shipType: parseAsString,
  startEffectiveFromDate: parseAsString,
  trailerKey: parseAsInteger,
};

export const getDefaultAccessorialsFormValues = (timeZone = getLocalTimeZone()): AccessorialsFilters => ({
  accessorialCode: null,
  accessorialKey: null,
  branch: null,
  carrier: null,
  currency: null,
  destinationCity: null,
  destinationCountry: null,
  destinationLocationKey: null,
  destinationPortKey: null,
  destinationPostalFrom: null,
  destinationPostalTo: null,
  destinationState: null,
  directInterline: null,
  effectiveFromDate: toZonedDayIso(timeZone),
  effectiveToDate: toZonedDayIso(timeZone, DEFAULT_MONTH_RANGE, true),
  endEffectiveToDate: undefined,
  equipmentType: null,
  isDangerousGoods: null,
  laneKey: null,
  originCity: null,
  originCountry: null,
  originLocationKey: null,
  originPortKey: null,
  originPostalFrom: null,
  originPostalTo: null,
  originState: null,
  pricingType: null,
  service: null,
  shipType: null,
  startEffectiveFromDate: undefined,
});

export const useAccessorialsFilters = () => {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const [rawSearchParams, setSearchParams] = useQueryStates(parsers, {
    clearOnDefault: false,
    history: 'replace',
  });

  const customerKey = selectedCompany?.companyId ?? 0;

  const searchParams = useMemo(() => {
    const cleanedRawSearchParams = cleanNuqsURLParams(rawSearchParams);
    const result = v.safeParse(accessorialsFiltersApiSchema, cleanedRawSearchParams);

    if (!result.success) {
      console.error('Invalid URL params', result.issues);
      showNotification({ message: `Invalid URL params`, type: NOTIFICATION_TYPES.ERROR });
      return {
        customerKey,
        effectiveFromDate: parsers.effectiveFromDate.defaultValue,
        effectiveToDate: parsers.effectiveToDate.defaultValue,
      } as AccessorialsFiltersApi;
    }

    return {
      ...result.output,
      customerKey: result.output.customerKey || customerKey,
    };
  }, [rawSearchParams, customerKey]);

  const { data: filters } = useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => normalizeAccessorialsFilters(searchParams),
    queryKey: ['accessorials-filters', searchParams],
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

  const onFiltersChange = (updates: Partial<AccessorialsFilters>) => {
    const nextSearchParams = normalizeAccessorialsFiltersUpdate(
      {
        ...getDefaultAccessorialsFormValues(),
        ...filters,
        ...updates,
      },
      customerKey,
    );

    setSearchParams(
      Object.entries(nextSearchParams).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined && value !== '') {
          acc[key] = value;
        } else {
          acc[key] = null;
        }
        return acc;
      }, {}) as Parameters<typeof setSearchParams>[0],
    );
  };

  const clearFilters = useCallback(() => {
    return setSearchParams(
      Object.fromEntries(Object.keys(parsers).map((key) => [key, null])) as Parameters<typeof setSearchParams>[0],
    );
  }, [setSearchParams]);

  return {
    clearFilters,
    customerKey,
    filters,
    onFiltersChange,
  };
};
